import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CpuChipIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { LandingHeader } from '../components/landing/LandingHeader';
import { LandingFooter } from '../components/landing/LandingFooter';
import { COMPANY_BRANDING } from '../config/companyBranding';
import '../styles/landing/landing.css';
import '../styles/landing/company-landing.css';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type SolutionCard = {
  title: string;
  description: string;
  points: string[];
  icon: IconComponent;
};

type SegmentCard = {
  title: string;
  description: string;
  outcomes: string[];
};

const SOLUTION_PILLARS: SolutionCard[] = [
  {
    title: 'Secure Operations',
    description:
      'Role-aware workflows, governed routing, and access controls built for accountability in high-stakes environments.',
    points: ['Organization and domain isolation', 'Audit-ready activity trails', 'Policy-driven permissions'],
    icon: ShieldCheckIcon,
  },
  {
    title: 'Intelligent Workload Automation',
    description:
      'Smart assistants and real-time collaboration tools that reduce repetitive admin effort and communication delays.',
    points: ['Live communication hub', 'AI-assisted routing and summaries', 'Faster handovers and follow-through'],
    icon: CpuChipIcon,
  },
  {
    title: 'Data Protection by Design',
    description:
      'Built with secure architecture patterns so people can trust our software with their most sensitive information.',
    points: ['Encryption-forward design', 'Compliance-ready architecture', 'Controlled data visibility'],
    icon: LockClosedIcon,
  },
];

const DELIVERY_SEGMENTS: SegmentCard[] = [
  {
    title: 'Hospital and Multi-Site Networks',
    description: 'Coordinate care teams, handovers, and urgent communication from one secure platform.',
    outcomes: ['Standardized communication flows', 'Cross-team escalation support'],
  },
  {
    title: 'Clinics and Specialty Practices',
    description: 'Streamline practice-level operations with less admin overhead and stronger patient continuity.',
    outcomes: ['Reduced workflow friction', 'Clear follow-up accountability'],
  },
  {
    title: 'Individuals and Everyday Teams',
    description:
      'Personal tools for finances and daily organization, built with the same security discipline as our enterprise platforms.',
    outcomes: ['Clarity and control over daily life', 'Privacy-first by default'],
  },
];

export const AimepicCompanyLandingPage: React.FC = () => {
  useEffect(() => {
    const rootElement = document.getElementById('root');
    document.documentElement.classList.add('company-page-scroll');
    document.body.classList.add('company-page-scroll');
    rootElement?.classList.add('company-page-scroll-root');

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || 'GA_MEASUREMENT_ID', {
        page_title: COMPANY_BRANDING.meta.defaultTitle,
        page_location: window.location.href,
        custom_map: { metric_1: 'page_load_time' },
      });
    }

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Aimepic Company Landing Page',
        page_location: window.location.pathname,
        event_category: 'engagement',
      });
    }

    return () => {
      document.documentElement.classList.remove('company-page-scroll');
      document.body.classList.remove('company-page-scroll');
      rootElement?.classList.remove('company-page-scroll-root');
    };
  }, []);

  const heroHeadlineLine1 = 'Secure, intelligent';
  const heroHeadlineLine2 = 'software for work & life';
  const heroSubline =
    'From hospital operations to personal finance — software that turns complexity into clarity, control, and reliable execution.';

  return (
    <>
      <Helmet>
        <title>{COMPANY_BRANDING.meta.defaultTitle}</title>
        <meta name="description" content={COMPANY_BRANDING.meta.defaultDescription} />
        <meta name="keywords" content={COMPANY_BRANDING.meta.keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.origin} />
        <meta property="og:title" content={COMPANY_BRANDING.meta.defaultTitle} />
        <meta property="og:description" content={COMPANY_BRANDING.meta.defaultDescription} />
        <meta property="og:url" content={window.location.origin} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={COMPANY_BRANDING.meta.defaultTitle} />
        <meta name="twitter:description" content={COMPANY_BRANDING.meta.defaultDescription} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: COMPANY_BRANDING.company.fullName,
            url: window.location.origin,
            logo: `${window.location.origin}/images/logo.png`,
            description: COMPANY_BRANDING.company.description,
            contactPoint: {
              '@type': 'ContactPoint',
              email: COMPANY_BRANDING.contact.email,
              contactType: 'Customer Service',
            },
          })}
        </script>
      </Helmet>

      <div className="landing-page company-landing">
        <LandingHeader isCompanyPage={true} />

        <section id="hero" className="company-hero" aria-labelledby="hero-heading">
          <div className="company-hero-grid" aria-hidden="true" />
          <div className="company-orb company-orb-one" aria-hidden="true" />
          <div className="company-orb company-orb-two" aria-hidden="true" />
          <div className="company-orb company-orb-three" aria-hidden="true" />
          <div className="company-orb company-orb-four" aria-hidden="true" />

          <div className="company-shell company-hero-layout">
            <div className="company-hero-copy">
              <p className="company-eyebrow">Intuitive Realities</p>
              <h1 id="hero-heading" className="company-hero-title">
                <span className="company-hero-title-line1">{heroHeadlineLine1}</span>
                <span className="company-hero-title-line2">{heroHeadlineLine2}</span>
              </h1>
              <p className="company-hero-subline">{heroSubline}</p>

              <div className="company-hero-actions">
                <Link to="/contact" className="company-btn company-btn-primary">
                  Talk to our team
                  <ArrowRightIcon className="company-btn-icon" aria-hidden="true" />
                </Link>
                <Link to="/onecare/" className="company-btn company-btn-secondary">
                  Explore OneCare
                </Link>
              </div>

              <div className="company-trust-chips" aria-label="Trust and security highlights">
                <span className="company-chip">
                  <ShieldCheckIcon className="company-chip-icon" aria-hidden="true" />
                  Enterprise-grade security
                </span>
                <span className="company-chip">
                  <LockClosedIcon className="company-chip-icon" aria-hidden="true" />
                  Domain-isolated architecture
                </span>
                <span className="company-chip">
                  <SparklesIcon className="company-chip-icon" aria-hidden="true" />
                  Workflow intelligence
                </span>
              </div>
            </div>
          </div>
        </section>

        <main id="main-content" role="main" className="company-main">
          <section
            id="onecare-intro"
            className="company-section company-onecare-hero"
            aria-labelledby="onecare-intro-heading"
          >
            <div className="company-shell company-onecare-hero-layout">
              <div className="company-onecare-hero-copy">
                <p className="company-section-eyebrow">Our Flagship Platform</p>
                <h2 id="onecare-intro-heading">Meet OneCare</h2>
                <p className="company-onecare-hero-lead">
                  OneCare is our healthcare operations platform — a secure workspace that unifies
                  real-time team communication, AI-assisted clinical workflows, and a unified patient
                  registry for hospitals, clinics, and multi-site practices.
                </p>
                <ul className="company-onecare-hero-highlights">
                  <li>
                    <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                    <span>
                      <strong>Intra-Communications</strong> — real-time channels, urgency routing, and audit-ready messaging.
                    </span>
                  </li>
                  <li>
                    <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                    <span>
                      <strong>Smart Flow AI</strong> — multi-model assistant for clinical summaries, analysis, and task workflows.
                    </span>
                  </li>
                  <li>
                    <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                    <span>
                      <strong>Patient Registry &amp; AI Scribe</strong> — unified inpatient, outpatient, ER, and ICU records with de-identified AI drafting.
                    </span>
                  </li>
                </ul>
                <div className="company-hero-actions">
                  <Link to="/onecare/" className="company-btn company-btn-primary">
                    Explore OneCare
                    <ArrowRightIcon className="company-btn-icon" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section id="solutions" className="company-section">
            <div className="company-shell">
              <header className="company-section-header">
                <p className="company-section-eyebrow">Solutions</p>
                <h2>Professional digital infrastructure for work that matters</h2>
                <p>
                  We design software systems that connect teams, secure sensitive workflows, and remove operational
                  bottlenecks — from hospital floors to personal finances.
                </p>
              </header>

              <div className="company-card-grid">
                {SOLUTION_PILLARS.map((pillar) => (
                  <article key={pillar.title} className="company-card">
                    <div className="company-card-icon-wrap">
                      <pillar.icon className="company-card-icon" aria-hidden="true" />
                    </div>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.description}</p>
                    <ul>
                      {pillar.points.map((point) => (
                        <li key={point}>
                          <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="customers" className="company-section">
            <div className="company-shell">
              <header className="company-section-header">
                <p className="company-section-eyebrow">Customers</p>
                <h2>Built for people solving real-world challenges</h2>
                <p>
                  We partner with healthcare providers, administrators, and individuals who need secure tools that
                  actually fit their daily work and lives.
                </p>
              </header>

              <div className="company-segment-grid">
                {DELIVERY_SEGMENTS.map((segment) => (
                  <article key={segment.title} className="company-segment-card">
                    <div className="company-segment-heading">
                      <UserGroupIcon className="company-stack-icon" aria-hidden="true" />
                      <h3>{segment.title}</h3>
                    </div>
                    <p>{segment.description}</p>
                    <ul>
                      {segment.outcomes.map((outcome) => (
                        <li key={outcome}>
                          <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="updates" className="company-section company-updates">
            <div className="company-shell company-updates-layout">
              <div>
                <p className="company-section-eyebrow">What&apos;s New</p>
                <h2>Future-ready innovation with enterprise discipline</h2>
                <p>
                  Aimepic continues to evolve around secure communication, intelligent automation, and scalable
                  operations. Our first product, OneCare, is live at aimepic.com/onecare — with new tools for
                  personal finance and everyday life in the works.
                </p>
              </div>
              <div className="company-updates-actions">
                <Link to="/onecare/" className="company-btn company-btn-primary">
                  Explore OneCare
                  <ArrowRightIcon className="company-btn-icon" aria-hidden="true" />
                </Link>
                <Link to="/contact" className="company-btn company-btn-ghost">
                  Contact Sales
                </Link>
              </div>
            </div>
          </section>
        </main>

        <LandingFooter isCompanyPage={true} />
      </div>
    </>
  );
};

export default AimepicCompanyLandingPage;
